import { EraQL, Expression, Markup, Relation, Ty } from '@cotera/era';
import { err, ok, Result } from 'neverthrow';
import _ from 'lodash';
import { Graph } from '../graph';
import { GRAPH_CONSTANTS } from './special-forms';

export type RelativeParser = {
  parse: (eraql: string) => EraQL.ParseRes<EraQL.CompileOutput>;
  parseExpr: (
    eraql: string,
    opts?: { bindings?: EraQL.Bindings }
  ) => EraQL.ParseRes<Expression>;
  parseRel: (eraql: string) => EraQL.ParseRes<Relation>;
  availableAttributes: () => string[];
  forRel: (name: string) => RelativeParser;
};

export class GraphRelativeParser implements RelativeParser {
  readonly baseCtx: EraQL.ParseCtx;

  constructor(private readonly graph: Record<string, Graph.GraphResult>) {
    this.baseCtx = graphParseCtx(graph);
  }

  parse(eraql: string): EraQL.ParseRes<EraQL.CompileOutput> {
    return EraQL.parse(eraql, this.baseCtx);
  }

  parseExpr(
    eraql: string,
    opts?: { bindings?: EraQL.Bindings }
  ): EraQL.ParseRes<Expression> {
    return EraQL.parseExpr(eraql, { ...this.baseCtx, ...opts });
  }

  parseRel(eraql: string): EraQL.ParseRes<Relation> {
    return EraQL.parseRel(eraql, this.baseCtx);
  }

  parseMu(eraql: string): EraQL.ParseRes<Markup> {
    return EraQL.parseMu(eraql, this.baseCtx);
  }

  parseExprInRel(
    relName: string,
    eraql: string,
    opts?: { bindings?: EraQL.Bindings }
  ): EraQL.ParseRes<Expression> {
    const ctx = this.buildRelContext(relName);

    return EraQL.parseExpr(eraql, { ...ctx, ...opts });
  }

  availableAttributes(): string[] {
    return Object.keys(this.baseCtx.attributes ?? {});
  }

  forRel(name: string): RelativeParser {
    const ctx = this.buildRelContext(name);
    return new GraphRelativeParserForRel(ctx, (name) =>
      this.buildRelContext(name)
    );
  }

  private buildRelContext(relName: string): EraQL.ParseCtx {
    const rel = this.graph[relName];

    let attributes: Record<string, Ty.ExtendedAttributeType> = {};

    if (rel !== undefined) {
      if (rel.isOk()) {
        if (rel.value.item.t !== 'rel') {
          throw new Error(
            `Logic Error: symbol "${relName}" is of type "${rel.value.item.t}"`
          );
        } else {
          attributes = rel.value.item.rel.attributes;
        }
      } else if (rel.isErr() && rel.error.partial?.t === 'rel') {
        attributes = rel.error.partial.attributes;
      }
    } else {
      throw new Error(`Logic Error: no symbol exists with name "${relName}"`);
    }

    return {
      ...this.baseCtx,
      attributes,
    };
  }
}

export class GraphRelativeParserForRel implements RelativeParser {
  constructor(
    private readonly ctx: EraQL.ParseCtx,
    private readonly buildRelContext: (name: string) => EraQL.ParseCtx
  ) {}

  parse(eraql: string): EraQL.ParseRes<EraQL.CompileOutput> {
    return EraQL.parse(eraql, this.ctx);
  }

  forRel(name: string): RelativeParser {
    const ctx = this.buildRelContext(name);
    return new GraphRelativeParserForRel(ctx, this.buildRelContext);
  }

  /**
   * deprecated
   * used for backwards compat with non-graph relative parsers
   */
  static fromRel(rel: Pick<Relation, 'attributes'>): GraphRelativeParserForRel {
    return new GraphRelativeParserForRel(
      {
        attributes: rel.attributes,
      },
      (_relName) => {
        throw new Error(
          `Logic Error: no symbol exists with name "${_relName}"`
        );
      }
    );
  }

  parseExpr(
    eraql: string,
    opts?: { bindings?: EraQL.Bindings }
  ): EraQL.ParseRes<Expression> {
    return EraQL.parseExpr(eraql, { ...this.ctx, ...opts });
  }

  parseRel(eraql: string): EraQL.ParseRes<Relation> {
    return EraQL.parseRel(eraql, this.ctx);
  }

  availableAttributes(): string[] {
    return Object.keys(this.ctx.attributes ?? {});
  }
}

const graphParseCtx = (
  items: Record<string, Graph.GraphResult>,
  opts?: { bindings?: EraQL.Bindings }
): EraQL.ParseCtx => {
  const ctx: EraQL.ParseCtx = {
    dynamicSymbolLookup: lookup({ stripLeading: '%', items }),
    bindings: {
      ...opts?.bindings,
      ...GRAPH_CONSTANTS,
      '%': {
        t: 'symbol-lookup',
        lookup: lookup({ items }),
      },
    },
  };

  return ctx;
};

export const lookup =
  (opts: {
    stripLeading?: string;
    items: Record<string, Graph.GraphResult>;
  }): EraQL.SymbolLookup =>
  (
    rawSymbol: string,
    _meta: EraQL.TokenMeta
  ): Result<EraQL.Item, readonly EraQL.ParseError[]> | null => {
    const { stripLeading = '' } = opts;

    if (!rawSymbol.startsWith(stripLeading)) {
      return null;
    }

    const lookupSym = rawSymbol.slice(stripLeading.length);

    const item = opts.items[lookupSym]
      ?.map((x) => x.item)
      .mapErr((e) => {
        const pe: EraQL.ParseError = {
          t: 'custom',
          // TODO: this is a pretty terrible error
          msg: `Dependency %"${lookupSym}" has error of type ${e.err.t}`,
        };

        return [pe];
      });

    if (item === undefined) {
      const closest = _.minBy(Object.keys(opts.items), (option) =>
        levenshtein(lookupSym, option)
      );
      const e: EraQL.ParseError = {
        t: 'custom',
        msg: `symbol "${lookupSym}" not found, did you mean?: "${closest}"`,
      };

      return err([e]);
    }

    if (item.isErr()) {
      return err(item.error);
    }

    const val = item.value;

    if (val.t === 'tool') {
      const pe: EraQL.ParseError = {
        t: 'custom',
        msg: `"${lookupSym}" refers to a tool, and can't be used in this context`,
      };

      return err([pe]);
    } else if (val.t === 'agent') {
      const pe: EraQL.ParseError = {
        t: 'custom',
        msg: `"${lookupSym}" refers to an agent, and can't be used in this context`,
      };

      return err([pe]);
    } else if (val.t === 'document') {
      const pe: EraQL.ParseError = {
        t: 'custom',
        msg: `"${lookupSym}" refers to a document, and can't be used in this context`,
      };

      return err([pe]);
    } else {
      return ok(val);
    }
  };

function levenshtein(a: string, b: string): number {
  const dp: number[][] = Array.from({ length: a.length + 1 }, () =>
    Array(b.length + 1).fill(0)
  );

  for (let i = 0; i <= a.length; i++) dp[i]![0] = i;
  for (let j = 0; j <= b.length; j++) dp[0]![j] = j;

  for (let i = 1; i <= a.length; i++) {
    for (let j = 1; j <= b.length; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      dp[i]![j] = Math.min(
        dp[i - 1]![j]! + 1, // deletion
        dp[i]![j - 1]! + 1, // insertion
        dp[i - 1]![j - 1]! + cost // substitution
      );
    }
  }

  return dp[a.length]![b.length]!;
}
